/* eslint-disable react-native/no-color-literals */
/* eslint-disable react-native/no-inline-styles */
import React, { FC, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import { observer } from "mobx-react-lite"
import { ViewStyle, ListRenderItem, useWindowDimensions, FlatList, Appearance , ScrollView } from "react-native"
import { color } from "../../../theme"
import { CompositeScreenProps } from "@react-navigation/native"
import { View, Icon } from "native-base"
import { Button, JalaIcon, Screen, Text } from "../../../components"
import Octicons from "react-native-vector-icons/Octicons"
import MaterialCommunityIcons from "react-native-vector-icons/MaterialCommunityIcons"
import { translate } from "../../../i18n"
import _ from "lodash"
import { AlertCompleteData, GraphChemical } from "./chemical-measurement-prediction-list-screen"
import moment from "moment"
import { useStores } from "../../../models"
import { FarmStoreModel } from "../../../models/app-store/farm-store/farm-store"
import { PondStoreModel } from "../../../models/app-store/farm-store/pond-store/pond-store"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import config from "../../../utils/config"
import { delay } from "../../../utils/delay"
import JalaAnalytic from "../../../utils/jala-analytic"

const ROOT: ViewStyle = {
  flex: 1,
  backgroundColor: color.palette.backgroundGray,
}

const alkalinityGroup = [
  18,
  19,
  14,
]

const hardnessGroup = [
  16,
  17,
  15,
]

const otherParameterGroup = [
  11,
  12,
  13,
  20
]

const ChemicalTable: FC<{
  colorScheme?: "light" | "dark",
  item: any,
  onScrollX?: (xState: number) => void,
  forwardRef?: React.Ref<{onPressSort: () => void}>,
  setCollapseHeader: (status: boolean) => void
} & CompositeScreenProps<any, any>> = observer((props) => {
  const {
    colorScheme = "light" || Appearance.getColorScheme(),
    route,
    onScrollX,
    forwardRef,
    setCollapseHeader
  } = props
  const {
    chemicalParameterStore: { data : chemicalParameter }, appStore: { farmStore }, chemicalPredictionsStore,
    chemicalMultiMeasurementStore: actualChemicalModelStore
  } = useStores();
  const {
    chemicalMultiMeasurements, pondStore: {
      cycleStore,
    }
  } = farmStore || FarmStoreModel.create({});
  const [sortType, setSortType] = useState<"desc"|"asc">("desc") // asc, desc
  const { title } = route.params;
  const dimensions = useWindowDimensions();

  useEffect(() => {
    JalaAnalytic.log("Mobile App : Chemical Prediction - Visit Table Prediction")
  }, [])

  useImperativeHandle(forwardRef, () => {
    return {
      onPressSort: () => {
        onPressSort()
      }
    }
  })

  useEffect(() => {
    if (chemicalPredictionsStore.data.length) {
      chemicalPredictionsStore.getSyncData(chemicalMultiMeasurements, sortType)
    }
  }, [chemicalMultiMeasurements, chemicalPredictionsStore.data.length, sortType])

  useEffect(() => {
    actualChemicalModelStore.init();
    cycleStore.getChemicalMultiMeasurements();
  }, [cycleStore, actualChemicalModelStore])

  const MIN_WIDTH_TABLE_ITEM = useMemo(() => {
    return dimensions.width * 1 / 5
  }, [dimensions])

  const chemicalsGroup = useMemo(() => {
    return title === "chemicalParameterScreen.alkalinity" ? alkalinityGroup :
      title === "chemicalParameterScreen.hardness" ? hardnessGroup : otherParameterGroup
  }, [title])

  const renderItemDateDoc: ListRenderItem<any> = useCallback(({item, index}) => {
    return (
      <View
        flexDirection={"row"}
        borderLeftWidth={"1px"} borderColor={color.palette.gray200}
        background={index % 2 === 0 ? color.palette.white : `rgba(245, 246, 247, 0.5)`}
        borderBottomWidth={"1px"} height={"40px"}
      >
        <View
          borderRightWidth={"1px"} borderColor={color.palette.gray200}
          width={"110px"} justifyContent={"center"} alignItems={"center"}
        >
          <Text
            text={moment(item.date || item.measured_at).format("DD/MM/YYYY")}
            preset="body2" colorScheme={colorScheme} />
        </View>
        <View
          borderRightWidth={"1px"} borderColor={color.palette.gray200}
          width="65px" justifyContent={"center"} alignItems={"center"}
        >
          <Text text={item.age || "-"} preset="body2" colorScheme={colorScheme} />
        </View>
      </View>
    )
  }, [colorScheme])

  const renderItem: ListRenderItem<any> = useCallback(({item, index}) => {    
    return (
      <View
        flexDirection={"row"}
        background={index % 2 === 0 ? color.palette.white : `rgba(245, 246, 247, 0.5)`}
        borderBottomColor={color.palette.gray200} borderBottomWidth={"1px"} height={"40px"}
      >
        {chemicalsGroup.map((chemical, index) => {
          const itemChemicalParameter = chemicalParameter?.find(parameter => chemical === parameter.id) || {}
          const predictionItem = item?.predictions?.find(value => value.parameter_id === chemical) || {}

          const actualItem = item?.actualData
          return (
            <View
              borderRightWidth={"1px"} borderColor={color.palette.gray200}
              flex={2} flexDirection={"row"}
              minWidth={MIN_WIDTH_TABLE_ITEM}
              key={`key-item-value-${index}`}
            >
              <Text
                color={actualItem && actualItem[itemChemicalParameter.code] && actualItem[itemChemicalParameter.code] <= parseFloat(itemChemicalParameter.high_limit)
                  && actualItem[itemChemicalParameter.code] > parseFloat(itemChemicalParameter.low_limit) || !actualItem ? color.palette.gray800 : color.palette.red600}
                text={actualItem && actualItem[itemChemicalParameter.code] ? actualItem[itemChemicalParameter.code].toFixed(2) : "-"} preset="body2"
                colorScheme={colorScheme} width={"100px"} textAlign={"center"} alignSelf={"center"}
              />
              <View top={0} bottom={0} width={"1px"} backgroundColor={color.palette.gray200} />
              <Text
                color={predictionItem && predictionItem?.value <= parseFloat(itemChemicalParameter.high_limit)
                  && predictionItem?.value > parseFloat(itemChemicalParameter.low_limit) || !predictionItem?.value ? color.palette.gray800 : color.palette.red600}
                text={predictionItem?.value?.toFixed(2) || "-"} preset="body2"
                colorScheme={colorScheme} width={"100px"} textAlign={"center"} alignSelf={"center"}
              />
            </View>
          )
        })}
      </View>
    )
  }, [MIN_WIDTH_TABLE_ITEM, chemicalMultiMeasurements, chemicalParameter, chemicalsGroup, colorScheme])

  const onPressSort = useCallback(() => {
    switch(sortType) {
      case "desc": setSortType("asc"); break;
      case "asc": setSortType("desc"); break;
      default : setSortType("desc");
    }
  }, [sortType])
  
  const onScrollList = useCallback((yState: number) => {
    scrollRef1.current.scrollToOffset({
      animated: false,
      offset: yState
    })
    if (yState > 20) {
      setCollapseHeader(true)
    } else {
      setCollapseHeader(false)
    }
  }, [setCollapseHeader])

  const scrollRef1 = useRef<FlatList>();
  const scrollRef2 = useRef();

  return (
    <View
      marginX="1px"
      justifyItems={"center"}
    >
      <View flexDirection="row">
        <View>
          <View
            flexDirection={"row"} height={title !== "chemicalParameterScreen.otherParameter" ? "70px" : "48px"}
            borderWidth={"1px"} borderTopLeftRadius="8px"
            borderColor={color.palette.gray200}
          >
            <View
              flexDirection={"row"} background={color.palette.gray100}
              borderRightWidth={"1px"} borderColor={color.palette.gray200}
              width={"110px"} paddingX="16px"
              alignItems={"center"}
            >
              <Text colorScheme={colorScheme} flex={1} tx="date" preset="title5" textAlign={"center"} />
              <Icon as={JalaIcon} name="unfold_more" size="16px" onPress={onPressSort} />
            </View>
            <View
              background={color.palette.gray100}
              borderRightWidth={"1px"} borderColor={color.palette.gray200}
              width={"65px"}
              justifyContent={"center"} alignItems={"center"}
            >
              <Text colorScheme={colorScheme} tx="doc" preset="title5" />
            </View>
          </View>
          <FlatList
            ref={scrollRef1}
            data={chemicalPredictionsStore.listSyncActualPredictions}
            scrollEnabled={false}
            renderItem={renderItemDateDoc}
            contentContainerStyle={{
              paddingBottom: 170,
            }}
            keyExtractor={(_, index) => `key-item-table-${index}`}
          />
        </View>
        <ScrollView
          horizontal
        >
          <View>
            <View
              flexDirection={"row"} height={title !== "chemicalParameterScreen.otherParameter" ? "70px" : "48px"}
              borderBottomWidth={"1px"} borderTopWidth={"1px"}  borderColor={color.palette.gray200}
              borderTopRightRadius="8px"
            >
              <View
                background={color.palette.gray100} flex={1}
                borderRightWidth={"1px"} borderColor={color.palette.gray200}
              >
                {title !== "chemicalParameterScreen.otherParameter" &&
                  <View
                    justifyContent={"center"}
                    paddingY={"3px"}
                  >
                    <Text colorScheme={colorScheme} alignSelf={"center"} preset="title6" tx={title} />
                  </View>
                }
                <View flex={1} flexDirection={"row"} justifyContent={"center"}>
                { chemicalsGroup.map((value, index) => {
                  const itemChemicalParameter = chemicalParameter.find(parameter =>
                    value === parameter.id
                  ) || {}

                  const nameText = itemChemicalParameter.code && itemChemicalParameter.code !== "alkalinity"
                  && itemChemicalParameter.code !== "hardness" ?
                    itemChemicalParameter.short_name : translate("chemicalParameterScreen.total")

                  const unitText = itemChemicalParameter.low_limit === 0 ? `< ${itemChemicalParameter.high_limit || "0"} ${itemChemicalParameter.unit || ""}` :
                  itemChemicalParameter.high_limit === 0 ?  `≥ ${itemChemicalParameter.low_limit || "0"} ${itemChemicalParameter.unit || ""}` :
                  `${itemChemicalParameter.low_limit || "0"}-${itemChemicalParameter.high_limit || "0"} ${itemChemicalParameter.unit || ""}`

                  return (
                    <View
                      background={color.palette.gray100}
                      borderRightWidth={index < chemicalsGroup.length - 1 ? "1px" : "0px"} borderColor={color.palette.gray200}
                      borderTopWidth={"1px"} minWidth={MIN_WIDTH_TABLE_ITEM}
                      justifyContent={"center"} key={`key-item-name-${index}`}
                    >
                      <Text paddingY={"3px"} flex={1} colorScheme={colorScheme} textAlign={"center"} text={`${nameText} ${unitText}`} preset="title6" />
                      <View flexDirection={"row"} borderTopWidth={"1px"} borderColor={color.palette.gray200}>
                        <Text height={"23px"} width={"100px"} tx="Actual" preset="body2" colorScheme={colorScheme} textAlign={"center"} />
                        <View top={0} bottom={0} width={"1px"} backgroundColor={color.palette.gray200} />
                        <Text height={"23px"} textAlign={"center"}  width={"100px"} text={_.upperFirst(translate("cyclePredictions.plural"))} preset="body2" colorScheme={colorScheme} />
                      </View>
                    </View>
                  )
                })}
                </View>
              </View>
            </View>
            <FlatList
              ref={scrollRef2}
              data={chemicalPredictionsStore.listSyncActualPredictions}
              renderItem={renderItem}
              onScroll={({nativeEvent}) => {
                onScrollList(nativeEvent.contentOffset.y)
              }}
              onScrollEndDrag={({nativeEvent}) => {
                onScrollList(nativeEvent.contentOffset.y)
              }}
              contentContainerStyle={{
                paddingBottom: 170
              }}
              keyExtractor={(_, index) => `key-item-table-${index}`}
            />
          </View>
        </ScrollView>
      </View>
    </View>
  )
})

export const ChemicalMeasurementPredictionDetailScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function FullscreenChemicalParameterScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), navigation, route } = props
  const [isTableShown, setIsTableShown] = useState<boolean>(true)
  const { title, onPressAlertWarning, type, filter } = route.params;
  const { appStore: { farmStore }, chemicalPredictionsStore, chemicalParameterStore: { data : chemicalParameter } } = useStores();
  const { waterQualityMultiMeasurements, pondStore } = farmStore || FarmStoreModel.create({});
  const {
    cycleId,
    cycleStore: {
      feedsPerDate: feedsPerDateModels,
    }
  } = pondStore || PondStoreModel.create({});
  const insets = useSafeAreaInsets();
  const { BASE_URL } = config();
  const [currentFilter, setCurrentFilter] = useState<'week' | 'month' | 'cycle'>(filter)

  const [collapseHeader, setCollapseHeader] = useState<boolean>(false)

  useEffect(() => {
    if (type === "table") {
      setIsTableShown(true)
    } else {
      setIsTableShown(false)
    }
  }, [type])

  useEffect(() => {
    if (isTableShown) {
      JalaAnalytic.log("Mobile App : Chemical Prediction - Visit Table Prediction", {
        title: title.replace("chemicalParameterScreen.", "")
      })
    } else {
      JalaAnalytic.log("Mobile App : Chemical Prediction - Visit Graphic Prediction", {
        title: title.replace("chemicalParameterScreen.", ""),
        filter: currentFilter,
      })
    }
  }, [isTableShown, currentFilter, title])

  const currentDataPrediction = useMemo(() => {
    if (pondStore?.cycle?.finished_at) {
      return true
    }
    return chemicalPredictionsStore.data.find(value => value.date === moment().format("YYYY-MM-DD"))
  }, [chemicalPredictionsStore]);

  const onPressAlertModal = useCallback(async (content: string) => {
    navigation.goBack()
    await delay(800)
    onPressAlertWarning(content)
  }, [navigation, onPressAlertWarning]);

  const lastMeasurement = useMemo(() => {
    const filteredData = waterQualityMultiMeasurements.filter(value => value.cycle_id === cycleId)
    return filteredData.length ? filteredData[0] : null
  }, [waterQualityMultiMeasurements, cycleId])

  const sourceUrls = useMemo(() => {
    const parameterCode = []
    if (title === "chemicalParameterScreen.alkalinity") {
      parameterCode.push("alkalinity")
    } else if (title === "chemicalParameterScreen.hardness") {
      parameterCode.push("hardness")
    } else {
      parameterCode.push(...["ammonia", "nitrit", "nitrat", "tom"])
    }

    return parameterCode.map(value => {
      const parameter = chemicalParameter.find(parameter =>
        parameter.code === value
      ) || {}

      let url = `${BASE_URL}/web_view/cycles/${cycleId}/measurement_prediction_charts?`
      url += `parameter_code=${value}`
      return {
        url,
        parameter
      }
    })
  }, [title, chemicalParameter, BASE_URL, cycleId])

  const onPressExitFullscreen = useCallback(() => {
    navigation.goBack()
  }, [navigation])

  return (
    <Screen
      style={{
        ...ROOT,
        paddingRight: insets.right,
        paddingLeft: insets.left,
      }}
      preset="fixed"
      backgroundColor={color.palette.backgroundGray}
      backgroundBar={color.primary}
      statusBar="light-content"
      hideNavigationBar={true}
    >
      { !collapseHeader && 
        <View
          backgroundColor={color.palette.white}
          shadow={1} borderRadius="4px" marginTop="10px"
          overflow={"hidden"}
        >
          <View
            alignItems="center"
            flexDirection={"row"} paddingX="16px" paddingY="3px"
            borderBottomWidth={"1px"} borderColor={color.palette.gray200}
          >
            <View>
              <Text tx={title} preset="title3" colorScheme={colorScheme} />
              <Text tx="chemicalParameterScreen.latestUpdateText" txOptions={{date: `${
                lastMeasurement ? moment(lastMeasurement.measured_date).format("DD MMM YYYY") + ` (DoC ${lastMeasurement.age})` : '-'
                }`}} preset="body3" colorScheme={colorScheme} />
            </View>
            <View flex={1} />
            <Button
              style={{
                height: 30,
                marginLeft: 12,
                borderWidth: 1.5,
                borderColor: !isTableShown ? color.palette.blueJalaBase : color.palette.lineDark
              }}
              testID="jala:id/graph-button" preset="link"
              onPress={() => {setIsTableShown(false)}}
            >
              <Icon
                as={<Octicons name="graph" />} margin={"2px"} size={"24px"}
                color={!isTableShown ? color.palette.blueJalaBase : color.palette.lineDark}
              />
            </Button>
            <Button
              style={{
                height: 30,
                marginLeft: 12,
                borderWidth: 1.5,
                borderColor: isTableShown ? color.palette.blueJalaBase : color.palette.lineDark
              }}
              testID="jala:id/table-button" preset="link"
              onPress={() => {setIsTableShown(true)}}
            >
              <Icon
                as={<MaterialCommunityIcons name="table-large" />} size={"24px"} margin="2px"
                color={isTableShown ? color.palette.blueJalaBase : color.palette.lineDark}
              />
            </Button>
          </View>
          { (!currentDataPrediction) &&
            <AlertCompleteData
              colorScheme={colorScheme}
              alertType={!currentDataPrediction ? "emptyData" : "lowAccuracyData"}
              onPressDetail={onPressAlertModal}
              justifyContent={"center"} alignItems={"center"}
            />
          }
        </View>
      }
      { isTableShown ?
        <ChemicalTable
          navigation={navigation} setCollapseHeader={(status) => isTableShown && setCollapseHeader(status)}
          route={route} colorScheme={colorScheme} item={{}}
        />
        :
        <FlatList
          data={sourceUrls}
          renderItem={({item}) => <GraphChemical
            filterContainerStyle={{
              marginTop: "10px",
              marginBottom: "10px",
              alignSelf: "flex-start",
              marginLeft: "16px"
            }}
            filter={filter}
            colorScheme={colorScheme} item={item}
            startCycle={moment(pondStore.cycle.started_at).format("YYYY-MM-DD")}
            finishCycle={moment(pondStore.cycle.finished_at || moment().format('YYYY-MM-DD')).format("YYYY-MM-DD")}
            isFinishedCycle={!!pondStore.cycle.finished_at}
            onSetFilter={setCurrentFilter}
            withTitle={sourceUrls.length > 1} />
          }
          contentContainerStyle={{
            backgroundColor: color.palette.white
          }}
          onScrollEndDrag={({nativeEvent}) => {            
            if (nativeEvent.contentOffset.y > 0.1) {
              setCollapseHeader(true)
            } else {
              setCollapseHeader(false)
            }
          }}
          keyExtractor={(_, index) => `item-graph-${index}`}
        />
      }
      <Button
        style={{
          backgroundColor: "rgba(0, 0, 0, 0.35)",
          bottom: 16, right: 16, position: 'absolute',
          height: 32, width: 32,
          justifyContent: 'center', alignItems: 'center'
        }}
        testID="jala:id/table-button" preset="link"
        onPress={onPressExitFullscreen}
      >
        <Icon
          as={<JalaIcon name={"fullscreen_exit"} />} size={"24px"} margin="1px"
          color={color.palette.white}
        />
      </Button>
    </Screen>
  )
})
